<template>
  <div
    class="purchase-sticky"
    :class="{
      [`purchase-sticky--${productStatus}`]: true,
      reduced: reducePurchase && !forceFull,
      'has-full-page-modal': modal.show || showTestimonialsModal,
      'size-selector-open': showSizeSelector,
      'purchase-sticky--tabs': hasPrecoOrStockLink,
    }"
  >
    <div class="overlay" @click="handleOverlayClick" />
    <div v-sticky="sticky" data-v-sticky-inner>
      <div class="purchase-sticky__inner">
        <div class="purchase__top d-flex justify-space-between align-end">
          <div class="d-flex flex-direction-column justify-end align-start">
            <purchase-timer
              :product-document="productDocument"
              :shopify-product="shopifyProduct"
              :is-refreshed="isRefreshed"
              class="d-md-none"
            />

            <div
              v-if="hasPrecoOrStockLink"
              class="d-md-none d-flex purchase__sell-channel mt-0-4"
            >
              <nuxt-link
                class="purchase__sell-channel__choice"
                :class="{ 'nuxt-link-active': productInPreco.current }"
                :to="productInPreco.to"
                >{{ $t('purchase.price.preco') }}</nuxt-link
              >
              <nuxt-link
                class="purchase__sell-channel__choice"
                :class="{ 'nuxt-link-active': productInStock.current }"
                :to="productInStock.to"
                >{{ $t('purchase.price.stock') }}</nuxt-link
              >
            </div>
          </div>

          <!-- <div class="purchase__market d-flex align-center mb-0-2">
        <span>{{ $t('purchase.already') }}</span>
        <number-animated
          ref="number"
          :from="numberOfSells.from"
          :to="numberOfSells.to"
          :duration="0.5"
          @complete="numberOfSells.from = numberOfSells.to"
        />
        <span>{{ $t('purchase.preco') }}</span>
      </div> -->

          <button
            class="button-as-link button-reducer"
            @click="handleReducePurchaseSticky"
          >
            <span class="visually-hidden">{{ $t('action.reduce') }}</span>

            <icon name="carret-down" class="text-color-tertiary" />
          </button>
        </div>
        <div class="purchase__inner">
          <transition name="fade">
            <div
              class="purchase__title d-flex justify-start align-center pt-0-4 pl-0-4 pr-0-4"
              :class="{
                'title-with-products-links': hasLinkedProduct,
              }"
            >
              <purchase-timer
                v-if="!hasPrecoOrStockLink"
                :product-document="productDocument"
                :shopify-product="shopifyProduct"
                :is-refreshed="isRefreshed"
                class="d-none d-md-flex"
              />

              <h1
                class="text-color-tertiary text-color-md-secondary text-secondary"
              >
                {{ productDocument.title }}
              </h1>

              <div v-if="hasLinkedProduct" class="d-md-none flex-shrink-0">
                <list-picker
                  :label="productDocument.linked_products_label || ''"
                  :value="currentLinkedProduct"
                  :values="linkedProducts"
                  as-link
                  as-toggle
                  :title="productDocument.title"
                  class="d-md-none"
                />
              </div>

              <div
                v-if="showPrices"
                class="prices d-flex align-center"
                :class="{ 'd-md-none': hasPrecoOrStockLink }"
              >
                <price
                  :price="price"
                  class="text-color-tertiary text-color-md-secondary"
                  :text="
                    showCompareAtPrice
                      ? $t('purchase.price.preco')
                      : $t('purchase.price.stock')
                  "
                />
                <price
                  v-if="showCompareAtPrice"
                  :compare="true"
                  :price="compareAtPrice"
                  :text="$t('purchase.price.stock')"
                />
              </div>

              <div v-if="hasTestimonials" class="d-none d-md-block">
                <button
                  class="btn-link"
                  :class="{
                    'btn-link--disabled': !canClickTestimonials,
                  }"
                  @click="handleShowTestimonials"
                >
                  <div class="d-flex align-baseline text-secondary lh-1 pt-0-2">
                    <stars :value="1" :total="1" />
                    <span class="ml-0-2">
                      <span>{{ productDocument.rating_average }}/5</span>
                    </span>

                    <span
                      class="text-small ml-0-6"
                      :class="{
                        'purchase-underline': canClickTestimonials,
                      }"
                    >
                      {{
                        $t(
                          canClickTestimonials
                            ? 'testimonial.see'
                            : 'testimonial.based_on_number',
                          {
                            number: productDocument.rating_number,
                          }
                        )
                      }}
                    </span>
                  </div>
                </button>
              </div>

              <div
                v-if="hasPrecoOrStockLink"
                class="d-none d-md-block w-100 mt-0-4"
              >
                <preco-or-stock-prices
                  :product-in-preco="productInPreco"
                  :product-in-stock="productInStock"
                  :price="price"
                  :compare-at-price="compareAtPrice"
                  :loading="loadingPrecoOrStock"
                >
                  <purchase-timer
                    :product-document="productDocument"
                    :shopify-product="shopifyProduct"
                    :is-refreshed="isRefreshed"
                  />
                </preco-or-stock-prices>
              </div>
            </div>
          </transition>

          <transition name="fade">
            <list-picker
              v-if="hasLinkedProduct"
              :label="productDocument.linked_products_label || ''"
              :value="currentLinkedProduct"
              :values="linkedProducts"
              as-link
              class="d-none d-md-block mt-2"
            />
          </transition>

          <transition name="fade">
            <div class="purchase__selectors">
              <div ref="purchaseColors" class="purchase__colors">
                <color-picker
                  v-if="colorsDimension.length >= 1"
                  v-model="selectedDimensions.color"
                  :colors="colorsDimension"
                  :product-document="productDocument"
                  :product-is-on-sale="productIsOnSale"
                  with-label
                  :show-third="
                    hasThirdDimension &&
                    productDocument.third_options?.list?.length > 0 &&
                    !canSelectThirdDimension
                  "
                  :third-dimension-name="thirdDimensionName"
                  :third-dimension-label="productDocument.third_option_label"
                  :third-dimension="selectedThirdDimension"
                  @input="onColorChange"
                />
              </div>

              <list-picker
                v-if="hasThirdDimension && canSelectThirdDimension"
                :label="productDocument.third_option_label"
                :value="selectedThirdDimension"
                :values="thirdDimensions"
                class="purchase__thirds"
                @input="onThirdDimensionChange"
              />

              <div v-if="!colorSoldOut" class="purchase__size-selector">
                <div
                  class="d-flex align-center justify-space-between mb-md-0-8"
                >
                  <p class="picker__label text-big text-secondary mb-0">
                    <span>{{ $t('label.size_only') }}</span>
                  </p>

                  <div
                    v-if="hasSizeGuide"
                    class="purchase__size__guides d-none d-md-flex align-baseline justify-end"
                  >
                    <button class="btn-link" @click="handleShowModal('size')">
                      <div class="text-secondary lh-1 pt-0-2">
                        <span class="text-small purchase-underline">
                          {{ $t('purchase.size-guide') }}
                        </span>
                      </div>
                    </button>

                    <div id="kleep-module-sizing-desktop"></div>
                  </div>
                </div>

                <button
                  class="purchase__size-picker"
                  :class="{
                    'is-open': showSizeSelectorDesktop,
                  }"
                  @click="handleShowSizeSelector"
                >
                  <span
                    class="inner d-flex align-center justify-center justify-md-space-between text-color-tertiary text-color-md-secondary text-secondary text-extra-small text-md-base w-600"
                  >
                    <span v-if="selectedVariant">
                      {{ selectedVariant.opt.size.label }}
                    </span>
                    <span v-else>
                      {{ $t('action.choose') }}
                      <span class="d-none d-md-inline">{{
                        $t('action.a_size')
                      }}</span>
                    </span>

                    <icon name="carret-down" />
                  </span>
                </button>
              </div>
            </div>
          </transition>

          <transition
            @before-enter="onSizeDesktopBeforeEnter"
            @enter="onSizeDesktopEnter"
            @before-leave="onSizeDesktopBeforeLeave"
            @leave="onSizeDesktopLeave"
          >
            <div
              v-if="showSizeSelectorDesktop && !colorSoldOut"
              class="purchase__size-selector-desktop"
            >
              <div
                class="size-picker pt-0-6 pb-0-6 pl-1 pr-1"
                :class="{
                  'size-picker--errors': errors.size !== ERRORS_TYPE.NONE,
                }"
              >
                <button
                  v-for="size in sizesDimension"
                  :key="size.id"
                  class="size-picker__item"
                  :class="{
                    'size-picker__item--active':
                      selectedVariant && selectedVariant.id === size.id,
                    'size-picker__item--unavailable': !size.available,
                  }"
                  @click="handleSelectSize(size.opt.size.value)"
                >
                  <span>
                    {{ size.opt.size.label }}
                  </span>
                  <i
                    v-if="showInventoryLeft(size)"
                    class="size-picker__item__quantity"
                  >
                    {{
                      $tc(
                        'product.small_almost_sold_out',
                        size.quantityAvailable,
                        {
                          number: size.quantityAvailable,
                        }
                      )
                    }}
                  </i>
                </button>
              </div>
            </div>
          </transition>

          <transition name="appear-top">
            <error-text
              v-if="errors.size !== ERRORS_TYPE.NONE"
              :text="$t(`purchase.errors.size.${errors.size}`)"
              class="text-right mb-0-6 pr-0-4"
            />
          </transition>

          <div
            v-if="colorSoldOut"
            class="purchase__sold-out"
            :class="{
              'mt-md-2': colorsDimension.length >= 1,
            }"
          >
            <p
              class="mb-0 text-small text-md-base text-color-tertiary text-color-md-secondary"
            >
              {{
                $t(
                  [10286453621069, 10286453784909].includes(
                    parseInt(productDocument.shopify_product.id, 10)
                  )
                    ? 'product.soldout_polaire'
                    : 'product.soldout_v2_stock'
                )
              }}
            </p>
          </div>
        </div>

        <transition name="appear-top">
          <error-block
            v-if="variantSoldOut && !colorSoldOut"
            class="purchase-sticky__errors"
            :class="{
              'mt-0-6': !hasPrecoOrStockLink,
              'mt-0 mt-md-0-6': hasPrecoOrStockLink,
            }"
          >
            <p class="text-small text-center mb-0">
              {{ $t('product.size-not-available') }}
            </p>
          </error-block>
        </transition>

        <div class="purchase-sticky__actions d-flex pt-0-6 pt-md-2">
          <primary-button
            id="AddToCart"
            :text="actionText"
            align="center"
            type="button"
            :loading="cartLoading"
            :error="onError"
            :skeleton="!isRefreshed"
            :disabled="variantSoldOut || colorSoldOut"
            no-shadow
            class="add-to-cart"
            :class="`color-${selectedDimensions.color}`"
            @click="addToCart"
          />
        </div>

        <transition name="appear-top">
          <error-block
            v-if="errors.addToCart !== ERRORS_TYPE.NONE"
            class="mt-0-6"
          >
            <p
              class="mb-0"
              v-html="$t(`purchase.errors.addToCart.${errors.addToCart}`)"
            />
          </error-block>
        </transition>

        <transition name="fade">
          <div
            class="purchase__bottom d-flex justify-space-between align-center"
          >
            <button
              class="btn-link d-block"
              @click="handleShowModal('delivery')"
            >
              <div
                class="link--delivery d-flex align-center text-color-tertiary text-color-md-secondary text-md-secondary text-extra-small text-md-small w-600 mb-0 pt-0-6 pt-md-2 pb-0-6 pb-md-2"
              >
                <icon name="box" class="mr-0-2 mr-md-0-6" />
                <span
                  v-if="productStatus === STATUS.PRECOMMANDE"
                  class="purchase-underline"
                >
                  <span>
                    {{ $t('purchase.estimated_delivery_small') }}
                  </span>
                  <span
                    v-if="!isRefreshed"
                    class="skeleton skeleton--bar d-inline-block"
                    style="width: 100px; vertical-align: middle"
                    >&nbsp;</span
                  >
                  <span v-else
                    >{{ $t('purchase.between_small', deliveryText.options)
                    }}<span v-if="deliveryText.ref === 'noDays'">{{
                      $t('purchase.contactCare')
                    }}</span></span
                  >
                </span>
                <span v-else class="purchase-underline">
                  {{ $t('purchase.delivery') }}&nbsp;{{
                    $t('purchase.inAWeek')
                  }}
                </span>
              </div>
            </button>

            <button
              v-if="hasTestimonials"
              class="d-flex d-md-none align-center text-extra-small text-color-tertiary w-600 text-underline mb-0 pt-0-6 pb-0-6"
              @click="handleShowTestimonials"
            >
              <icon name="favorite" class="mr-0-2" />
              <span>
                <span>{{ productDocument.rating_average }}/5</span>
                <span>({{ productDocument.rating_number }} avis)</span>
              </span>
            </button>
            <div
              v-else-if="productStatus === STATUS.PRECOMMANDE"
              class="d-md-none text-extra-small text-color-tertiary w-600 mb-0 pt-0-6 pb-0-6"
            >
              {{
                $tc('product.items_sold', nbItemsSold, {
                  number: nbItemsSold,
                })
              }}
            </div>

            <p
              v-if="productStatus === STATUS.PRECOMMANDE"
              class="d-none d-md-block mb-0 text-secondary text-small"
            >
              {{
                $tc('product.items_sold', nbItemsSold, {
                  number: nbItemsSold,
                })
              }}
            </p>
          </div>
        </transition>
      </div>
    </div>

    <purchase-modal
      :title="$t('purchase.choose_size')"
      :show="showSizeSelector"
      @hide="handleHideModal"
    >
      <template #top-label>
        <p class="picker__label mb-0 mr-1">
          <span> {{ $t('label.color') }}</span>
          <span>{{ colorName }}</span>
        </p>
      </template>

      <template #error>
        <transition name="appear-top">
          <error-text
            v-if="errors.size !== ERRORS_TYPE.NONE"
            :text="$t(`purchase.errors.size.${errors.size}`)"
            class="text-center mb-0-6 pr-0-4"
          />
        </transition>
      </template>

      <template #content>
        <div
          class="size-picker pt-0-6 pb-0-6 pl-1 pr-1"
          :class="{
            'size-picker--errors': errors.size !== ERRORS_TYPE.NONE,
          }"
        >
          <button
            v-for="size in sizesDimension"
            :key="size.id"
            class="size-picker__item"
            :class="{
              'size-picker__item--active':
                selectedVariant && selectedVariant.id === size.id,
              'size-picker__item--unavailable': !size.available,
            }"
            @click="handleSelectSize(size.opt.size.value)"
          >
            <span>
              {{ size.opt.size.label }}
            </span>
            <i
              v-if="showInventoryLeft(size)"
              class="size-picker__item__quantity"
            >
              {{
                $tc('product.small_almost_sold_out', size.quantityAvailable, {
                  number: size.quantityAvailable,
                })
              }}
            </i>
          </button>
        </div>
      </template>

      <template #bottom>
        <div
          v-if="hasSizeGuide"
          class="d-flex align-center align-md-baseline justify-space-between pl-1 pr-1"
        >
          <button class="button-as-link" @click="handleShowModal('size')">
            <div
              class="d-flex align-center text-label w-600 text-uppercase purchase-underline lh-1 mb-0 pt-1 pb-1"
            >
              <ceintre-icon style="width: 1rem; height: 1rem" class="mr-0-4" />
              <span>{{ $t('purchase.size-guide') }}</span>
            </div>
          </button>

          <div id="kleep-module-sizing"></div>
        </div>

        <div class="d-flex ml-1 mr-1">
          <primary-button
            id="AddToCartSize"
            :text="actionText"
            align="center"
            type="button"
            :loading="cartLoading"
            :error="onError"
            :skeleton="!isRefreshed"
            :disabled="variantSoldOut || colorSoldOut"
            no-shadow
            class="add-to-cart"
            :class="`color-${selectedDimensions.color}`"
            @click="addToCart"
          />
        </div>

        <transition name="appear-top">
          <error-block
            v-if="errors.addToCart !== ERRORS_TYPE.NONE"
            class="mt-0-6 ml-1 mr-1"
          >
            <p
              class="mb-0"
              v-html="$t(`purchase.errors.addToCart.${errors.addToCart}`)"
            />
          </error-block>
        </transition>
      </template>
    </purchase-modal>

    <purchase-modal
      v-if="hasTestimonials"
      :title="$t('product.testimonials.title')"
      :show="showTestimonialsModal"
      full-page
      desktop-override-purchase
      @hide="handleHideModal"
    >
      <template #top>
        <div class="purchase-testimonials d-flex align-end">
          <div>
            <p class="global-note mb-0-4">
              <strong class="h1 text-secondary mb-0">{{
                productDocument.rating_average
              }}</strong
              ><span class="text-small">&nbsp;/ 5</span>
            </p>

            <stars :value="productDocument.rating_average" />
          </div>

          <div class="separator"></div>

          <div class="flex-grow-1">
            <p
              class="text-extra-small text-md-small lh-1-2 mb-0-4"
              v-html="
                $t('product.testimonials.number', {
                  number: productDocument.rating_number,
                })
              "
            />

            <p class="text-extra-small text-md-small lh-1-2 mb-0">
              {{ $t('product.testimonials.text') }}
            </p>
          </div>
        </div>
      </template>

      <template #content>
        <testimonials-list
          v-if="showTestimonialsModal"
          :uid="testimonialsUid"
          class="pt-0-6 pl-1 pr-1 pb-1"
        />
      </template>

      <!-- <template #bottom>
        <div class="purchase-modal__bottom-action d-none d-md-block">
          <primary-button
            :text="actionText"
            align="center"
            type="button"
            no-shadow
            class="w-100"
            @click="handleHideModal"
          />
        </div>
      </template> -->
    </purchase-modal>

    <purchase-modal
      :title="modal.show ? $t(`purchase.modal.${modal.type}`) : ''"
      :show="modal.show"
      full-page
      desktop-override-purchase
      :class="`purchase-modal--page-${modal.type}`"
      @hide="handleHideModal"
    >
      <template #content>
        <transition mode="out-in" name="fade">
          <div v-if="modal.loading" class="mt-1">
            <p class="d-flex justify-center align-center">
              <loading-icon class="mr-0-6" />
              <span>{{ $t('loading') }}</span>
            </p>
          </div>

          <div v-else-if="modal.content">
            <blocks
              v-if="modal.content.blocks"
              :blocks="modal.content.blocks"
              :entities="{
                activeSize:
                  selectedVariant && selectedVariant.opt.size
                    ? selectedVariant.opt.size.value
                    : null,
              }"
            />
          </div>
        </transition>
      </template>

      <!-- <template #bottom>
        <div class="purchase-modal__bottom-action d-none d-md-block">
          <primary-button
            :text="actionText"
            align="center"
            type="button"
            no-shadow
            class="w-100"
            @click="handleHideModal"
          />
        </div>
      </template> -->
    </purchase-modal>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import { isWithinInterval } from 'date-fns'
import NuxtSSRScreenSize from 'nuxt-ssr-screen-size'
import slugify from 'slugify'

import Price from '@/components/Price/Price'
import ColorPicker from '@/components/picker/ColorPicker'
import PrimaryButton from '@/components/buttons/Primary'
import Icon from '@/components/icons/Icon'
import LoadingIcon from '@/components/icons/Loading'
import CeintreIcon from '@/components/icons/Ceintre'
import Blocks from '@/components/blocks/Blocks'
import Stars from '@/components/stars/Stars'

import PurchaseModal from '@/components/modal/PurchaseModal'
import PurchaseTimer from '@/components/Purchase/Timer'

import ErrorText from '@/components/errors/Text'
import ErrorBlock from '@/components/errors/Block'

import {
  isProductSaleable,
  PRODUCT_COLOR_STATUS,
} from '@/entities/sanity/product'

import { ERRORS_TYPE, SHOPIFY_TYPES, SS_UPSELLS_ITEMS, STATUS } from '@/const'
import { EVENTS } from '@/const/events'

import DeliveryMixin from '@/mixins/deliveryText'

import services from '@/services'

import { getSanityBunnyImageLink } from '@/utils/image'
import ShopifyBase64 from '@/utils/shopifyBase64'
import ShopifyProduct from '@/entities/shopify/Product'

export default {
  name: 'PurchaseSticky',
  components: {
    Price,
    ColorPicker,
    PrimaryButton,
    ErrorText,
    ErrorBlock,
    Icon,
    LoadingIcon,
    Blocks,
    CeintreIcon,
    PurchaseModal,
    PurchaseTimer,
    Stars,
    PrecoOrStockPrices: () =>
      import(
        '@/components/Price/PrecoOrStockPrices' /* webpackChunkName: 'preco_or_stock_prices' */
      ),
    ListPicker: () =>
      import(
        '@/components/picker/ListPicker' /* webpackChunkName: 'list_picker' */
      ),
    TestimonialsList: () =>
      import(
        '@/components/testimonials/List' /* webpackChunkName: 'testimonials_list' */
      ),
    // NumberAnimated: () => ({
    //   component: import(
    //     '@/components/number/Number' /* webpackChunkName: 'animated_number' */
    //   ),
    // }),
  },
  mixins: [NuxtSSRScreenSize.NuxtSSRScreenSizeMixin, DeliveryMixin],
  props: {
    productDocument: { type: Object, required: true },
    shopifyProduct: { type: Object, required: true },
    forceFull: { type: Boolean, default: false },
  },
  data() {
    return {
      ERRORS_TYPE,
      STATUS,
      selectedDimensions: {
        color: null,
        size: null,
        third: null,
      },
      numberOfSells: { from: 0, to: 0 },
      isRefreshed: true,

      loadingPrecoOrStock: true,
      precoOrStockShopifyProduct: null,

      errors: {
        size: ERRORS_TYPE.NONE,
        addToCart: ERRORS_TYPE.NONE,
      },
      reducePurchase: false,
      showSizeSelector: false,
      showSizeSelectorDesktop: false,
      showTestimonialsModal: false,

      modal: {
        show: false,
        type: '',
        loading: false,
        content: null,
      },

      sticky: {
        containerSelector: '[data-v-sticky-container]',
        innerWrapperSelector: '[data-v-sticky-inner]',
        resizeSensor: true,
        minWidth: 769,
        bottomSpacing: 48,
      },
    }
  },
  computed: {
    ...mapGetters({
      cart: 'cart/cart',
      cartLoading: 'cart/isLoading',
      authenticated: 'auth/authenticated',
      customer: 'auth/customer',
      connectedEmail: 'subscriptions/connectedEmail',
    }),

    isDesktop() {
      return this.$vssWidth >= 769
    },
    productStatus() {
      if (this.shopifyProduct.hasDates) {
        return this.shopifyProduct.isSaleableAsStock
          ? STATUS.STOCK
          : STATUS.AGENDA
      }

      return this.productDocument.status
    },

    orderType() {
      if (this.productStatus === STATUS.PRECOMMANDE) {
        return 'preorder'
      }
      return 'stock'
    },
    productIsOnSale() {
      if (this.shopifyProduct.hasDates) {
        return this.shopifyProduct.isSaleableAsStock
      }

      return isProductSaleable(this.productDocument)
    },

    /**
     * Prix du produit
     */
    showPrices() {
      return this.productDocument && this.shopifyProduct
    },
    price() {
      if (!this.shopifyProduct) {
        return null
      }

      if (this.selectedDimensions.color) {
        return this.shopifyProduct.priceByDimension(
          'color',
          this.selectedDimensions.color
        )
      }

      return this.shopifyProduct.price
    },
    compareAtPrice() {
      if (!this.shopifyProduct) {
        return null
      }

      if (this.selectedDimensions.color) {
        return this.shopifyProduct.compareAtPriceByDimension(
          'color',
          this.selectedDimensions.color
        )
      }

      return this.shopifyProduct.compareAtPrice
    },
    showCompareAtPrice() {
      if (this.showPrices) {
        return (
          this.productStatus !== STATUS.STOCK &&
          this.price !== this.compareAtPrice
        )
      }

      return false
    },

    /**
     * Couleurs
     */
    colorName() {
      if (!this.productDocument.colors && !this.selectedDimensions.color) {
        return null
      }

      let color = null
      if (this.hasThirdDimension && this.canSelectThirdDimension) {
        color = this.productDocument.colors.find(
          (color) =>
            color._key ===
            `${this.selectedDimensions.third}__${this.selectedDimensions.color}`
        )
      } else {
        color = this.productDocument.colors.find(
          (color) =>
            this.getColorFromColorKey(color._key) ===
            this.selectedDimensions.color
        )
      }

      return color ? color.name : null
    },
    colorsDimension() {
      const cc = []

      for (const color of this.productDocument.colors) {
        if (
          this.shopifyProduct.isDimensionHasValue(
            'color',
            this.getColorFromColorKey(color._key)
          )
        ) {
          let shouldAddColor = true
          if (this.canSelectThirdDimension && this.selectedDimensions.third) {
            const colorsOfSelectedThirdDimension =
              this.shopifyProduct.getDimensionsByDimension(
                this.thirdDimensionName,
                this.selectedDimensions.third,
                'color'
              )

            shouldAddColor =
              colorsOfSelectedThirdDimension.includes(
                this.getColorFromColorKey(color._key)
              ) &&
              this.getThirdFromColorKey(color._key) ===
                this.selectedDimensions.third
          }

          if (!cc.some((c) => c.value === color._key) && shouldAddColor) {
            let label = color.label

            if (this.hasThirdDimension) {
              label =
                this.productDocument.shopify_product.colorOptions.find(
                  (colorOption) =>
                    this.getColorFromColorKey(color._key) === colorOption._key
                )?.name || label
            } else {
              label =
                this.productDocument.shopify_product.colorOptions.find(
                  (colorOption) => colorOption._key === color._key
                )?.name || label
            }

            const c = {
              value: this.getColorFromColorKey(color._key),
              label,
              isNew: color.color_status === PRODUCT_COLOR_STATUS.NEW,
              isLeavingSoon:
                color.color_status === PRODUCT_COLOR_STATUS.LEAVING_SOON,
              image: {
                url: getSanityBunnyImageLink(color.thumbnail, {
                  width: 100,
                  height: 100,
                }),
                dimensions: {
                  width: 100,
                  height: 100,
                },
              },
            }

            if (this.canSelectThirdDimension) {
              const mixedDimension = {
                color: c.value,
              }
              mixedDimension[this.thirdDimensionName] =
                this.selectedDimensions.third

              c.available =
                this.shopifyProduct.isMixedDimensionsAvailable(mixedDimension)

              c[this.thirdDimensionName] =
                this.shopifyProduct.getDimensionsByDimension(
                  'color',
                  c.value,
                  this.thirdDimensionName
                )[0]
            } else {
              c.available = this.shopifyProduct.isDimensionAvailable(
                'color',
                c.value
              )

              if (this.hasThirdDimension) {
                c[this.thirdDimensionName] =
                  this.shopifyProduct.getDimensionsByDimension(
                    'color',
                    c.value,
                    this.thirdDimensionName
                  )[0]
              }
            }
            // if (this.productIsOnSale && !c.available) {
            //   continue
            // }

            cc.push(c)
          }
        }
      }

      return cc
    },

    /**
     * Taille
     */
    sizesDimension() {
      if (this.selectedDimensions.color !== '') {
        if (this.hasThirdDimension) {
          return this.shopifyProduct.filterVariantsByDimensions([
            {
              dimension: 'color',
              value: this.selectedDimensions.color,
            },
            {
              dimension: this.thirdDimensionName,
              value: this.selectedDimensions.third,
            },
          ])
        }
        return this.shopifyProduct.filterVariantsByDimension(
          'color',
          this.selectedDimensions.color
        )
      }

      return []
    },

    /**
     * Troisieme dimension
     */
    hasThirdDimension() {
      return (
        this.productDocument.shopify_product.options.length > 2 &&
        this.productDocument.shopify_product.options.some(
          (o) => o.position === 3
        )
      )
    },
    thirdDimensionName() {
      if (this.hasThirdDimension) {
        return slugify(
          this.productDocument.shopify_product.options.find(
            (o) => o.position === 3
          ).name,
          { lower: true, strict: true }
        )
      }

      return ''
    },
    thirdDimensions() {
      const dd = []

      if (this.hasThirdDimension && this.productDocument.third_options.list) {
        for (const d of this.productDocument.third_options.list) {
          if (
            this.shopifyProduct.isDimensionHasValue(
              this.thirdDimensionName,
              slugify(d.name, { lower: true, strict: true })
            )
          ) {
            dd.push({
              value: slugify(d.name, { lower: true, strict: true }),
              label: d.name,
              desc: d.desc,
              image: d.image,
              available: this.shopifyProduct.isDimensionAvailable(
                this.thirdDimensionName,
                slugify(d.name, { lower: true, strict: true })
              ),
            })
          }
        }
      }

      return dd
    },
    canSelectThirdDimension() {
      return (
        this.hasThirdDimension &&
        this.productDocument.third_options.layout === 'as_selector'
      )
    },
    selectedThirdDimension() {
      if (this.hasThirdDimension) {
        return (
          this.thirdDimensions.find(
            (d) => d.value === this.selectedDimensions.third
          ) || null
        )
      }

      return null
    },

    /**
     * Produits liés
     */
    hasLinkedProduct() {
      return (
        this.productDocument &&
        this.productDocument.has_linked_product &&
        this.productDocument.linked_products &&
        this.productDocument.linked_products.length > 0
      )
    },
    linkedProducts() {
      if (this.hasLinkedProduct) {
        return this.productDocument.linked_products
          .filter((lp) => lp && lp.product?.uid)
          .map((lp) => ({
            value: lp.product.uid,
            label: lp.label,
            image: lp.image,
            to: this.$contextSanityPath(lp.product),
          }))
      }

      return []
    },
    currentLinkedProduct() {
      if (this.hasLinkedProduct) {
        return this.linkedProducts.find(
          (lp) => lp.value === this.productDocument.uid
        )
      }

      return null
    },

    /**
     * Variante sélectionnée
     */
    selectedVariant() {
      if (this.canSelectThirdDimension) {
        return this.shopifyProduct.variants.find(
          (v) =>
            v.opt.color.value === this.selectedDimensions.color &&
            v.opt.size.value === this.selectedDimensions.size &&
            v.opt[this.thirdDimensionName].value ===
              this.selectedDimensions.third
        )
      }
      return this.shopifyProduct.variants.find(
        (v) =>
          v.opt.color.value === this.selectedDimensions.color &&
          v.opt.size.value === this.selectedDimensions.size
      )
    },

    variantSoldOut() {
      if (this.selectedVariant) {
        return !this.selectedVariant.available
      }

      return false
    },

    /**
     * Plus de stock
     */
    colorSoldOut() {
      // if (this.selectedVariant) {
      //   return this.selectedVariant && !this.selectedVariant.available
      // }

      if (this.selectedDimensions.color === '') {
        // On ne peut pas savoir si le produit est sold out si aucune couleur n'est sélectionnée
        return false
      }

      if (this.hasThirdDimension) {
        return !this.shopifyProduct.isMixedDimensionsAvailable({
          color: this.selectedDimensions.color,
          [this.thirdDimensionName]: this.selectedDimensions.third,
        })
      }

      return !this.shopifyProduct.isDimensionAvailable(
        'color',
        this.selectedDimensions.color
      )
    },

    /**
     * Date de livraison
     */
    delivery() {
      return this.shopifyProduct.metafield
        ? new Date(this.shopifyProduct.metafield)
        : undefined
    },
    deliveryText() {
      if (
        this.productStatus === STATUS.PRECOMMANDE &&
        this.delivery &&
        !isNaN(this.delivery.getTime())
      ) {
        return this.getDeliveryFromDate(this.delivery)
      }

      if (this.productStatus === STATUS.STOCK) {
        return { ref: 'purchase.inAWeek', options: {} }
      }

      return { ref: 'purchase.noDays', options: {} }
    },

    hasSizeGuide() {
      return !!this.productDocument.size_guide?._ref
    },

    /**
     * Render Computed
     */
    hasTestimonials() {
      return (
        this.productDocument.rating_average !== null &&
        this.productDocument.rating_number !== null
      )
    },
    testimonialsUid() {
      return this.productDocument.custom_rating_uid || this.productDocument.uid
    },
    canClickTestimonials() {
      return this.$i18n.locale.startsWith('fr') && this.testimonialsUid
    },
    actionText() {
      if (this.productStatus === STATUS.STOCK) {
        return this.$t('action.buy')
      }

      return this.$t('action.preorder')
    },

    nbItemsSold() {
      return (
        this.shopifyProduct.totalInventory + this.productDocument.stock_initial
      )
    },

    /**
     * Liaison vers la page de précommande ou de stock
     */
    hasPrecoOrStockLink() {
      return (
        this.productDocument &&
        this.shopifyProduct &&
        this.productDocument.product_as_preco_or_stock !== null &&
        this.productDocument.product_as_preco_or_stock.uid !== null
      )
    },
    productInPreco() {
      if (this.hasPrecoOrStockLink) {
        if (this.productDocument.status === STATUS.PRECOMMANDE) {
          return {
            document: this.productDocument,
            shopifyProduct: this.shopifyProduct,
            to: {
              path: this.$contextSanityPath(this.productDocument),
              query: {
                color: this.selectedDimensions.color
                  ? this.selectedDimensions.color
                  : undefined,
              },
            },
            price: this.price,
            current: true,
          }
        }

        return {
          document: this.productDocument.product_as_preco_or_stock,
          shopifyProduct: this.precoOrStockShopifyProduct,
          to: {
            path: this.$contextSanityPath(
              this.productDocument.product_as_preco_or_stock
            ),
            query: {
              color: this.selectedDimensions.color
                ? this.selectedDimensions.color
                : undefined,
            },
          },
          price: this.precoOrStockShopifyProduct?.price || 0,
          current: false,
        }
      }
      return null
    },
    productInStock() {
      if (this.hasPrecoOrStockLink) {
        if (this.productDocument.status === STATUS.STOCK) {
          return {
            document: this.productDocument,
            shopifyProduct: this.shopifyProduct,
            to: {
              path: this.$contextSanityPath(this.productDocument),
              query: {
                color: this.selectedDimensions.color
                  ? this.selectedDimensions.color
                  : undefined,
              },
            },
            price: this.compareAtPrice,
            current: true,
          }
        }

        return {
          document: this.productDocument.product_as_preco_or_stock,
          shopifyProduct: this.precoOrStockShopifyProduct,

          to: {
            path: this.$contextSanityPath(
              this.productDocument.product_as_preco_or_stock
            ),
            query: {
              color: this.selectedDimensions.color
                ? this.selectedDimensions.color
                : undefined,
            },
          },
          price: this.precoOrStockShopifyProduct?.compareAtPrice || 0,
          current: false,
        }
      }

      return null
    },

    onError() {
      if (
        this.errors.size !== ERRORS_TYPE.NONE ||
        this.errors.addToCart !== ERRORS_TYPE.NONE
      ) {
        return true
      }

      return false
    },

    /**
     * From user state
     */
    productHasBeenAccessedFromUpsell() {
      if (process.client) {
        let upsellsId = window.sessionStorage.getItem(SS_UPSELLS_ITEMS)
        if (upsellsId) {
          upsellsId = JSON.parse(upsellsId)
          return (
            upsellsId.includes(this.shopifyProduct.id) ||
            this.$route.query.upsell === 'true'
          )
        }
      }
      return this.$route.query.upsell === 'true'
    },
  },
  watch: {
    showSizeSelector(val) {
      if (val) {
        this.$track.openPurchaseSizeSelector()
      }
    },
    showTestimonialsModal(val) {
      if (val) {
        this.$track.openPurchaseTestimonials()
      }
    },
    selectedVariant() {
      this.updateUrlParams()
    },
  },
  beforeDestroy() {
    this.$nuxt.$off(EVENTS.OPEN_TESTIMONIAL_MODAL)
  },
  created() {
    this.updateSelectedColorAndThird(false)

    this.numberOfSells = {
      from: 0,
      to:
        this.shopifyProduct.totalInventory + this.productDocument.stock_initial,
    }

    if (this.productStatus === STATUS.STOCK) {
      this.showSizeSelectorDesktop = true
    }
  },
  mounted() {
    this.updateSelectedColorAndThird(true)
    this.updateSelectedSize()

    this.updateHasColorsOverflow()

    this.initKleep()

    this.init()

    this.updateState()
  },
  methods: {
    ...mapActions({
      addItemToCart: 'cart/addItems',
    }),
    /**
     * Permet d'extraire la couleur de la clé couleur d'un productDocument.colors[]_key
     * @param colorKey La clé de la couleur (avec ou sans la 3ème dimension) selon le format "third__color"
     * @returns La couleur correspondante
     */
    getColorFromColorKey(colorKey) {
      return this.hasThirdDimension ? colorKey.split('__')[1] : colorKey
    },
    /**
     * Permet d'extraire la troisième dimension de la clé couleur d'un productDocument.colors[]_key
     * @param colorKey La clé de la couleur (avec ou sans la 3ème dimension) selon le format "third__color"
     * @returns La third correspondante
     */
    getThirdFromColorKey(colorKey) {
      return this.hasThirdDimension ? colorKey.split('__')[0] : colorKey
    },
    getColorKeyFromDimensions(dimensions) {
      if (this.hasThirdDimension) {
        return `${dimensions.third}__${dimensions.color}`
      }
      return dimensions.color
    },

    async updateSelectedColorAndThird(mounted = false) {
      if (
        this.productDocument.colors &&
        this.productDocument.colors.length > 0
      ) {
        if (mounted && this.$route.query.variant && this.shopifyProduct) {
          const variant = this.shopifyProduct.variants.find(
            (v) => v.id === parseInt(this.$route.query.variant)
          )

          if (variant) {
            if (this.hasThirdDimension && this.canSelectThirdDimension) {
              if (
                this.productDocument.colors.some(
                  (c) =>
                    c._key ===
                    `${variant.opt[this.thirdDimensionName].value}__${
                      variant.opt.color.value
                    }`
                )
              ) {
                this.selectedDimensions.color = variant.opt.color.value
                this.selectedDimensions.third =
                  variant.opt[this.thirdDimensionName].value
                return
              }
            } else if (this.hasThirdDimension) {
              if (
                this.productDocument.colors.some(
                  (c) =>
                    c._key ===
                    `${variant.opt[this.thirdDimensionName].value}__${
                      variant.opt.color.value
                    }`
                )
              ) {
                this.selectedDimensions.color = variant.opt.color.value
                this.selectedDimensions.third =
                  variant.opt[this.thirdDimensionName].value
                return
              } else if (
                this.productDocument.colors.some(
                  (c) =>
                    this.getColorFromColorKey(c._key) ===
                    variant.opt.color.value
                )
              ) {
                this.selectedDimensions.color = variant.opt.color.value
              }
            } else if (
              this.productDocument.colors.some(
                (c) =>
                  this.getColorFromColorKey(c._key) === variant.opt.color.value
              )
            ) {
              this.selectedDimensions.color = variant.opt.color.value
              return
            }
          }
        }

        if (mounted && this.$route.query.color) {
          if (this.hasThirdDimension && this.canSelectThirdDimension) {
            if (this.$route.query[this.thirdDimensionName]) {
              // IF the third dimension is selectable
              // THEN we have to search the color with its color name and the third dimension
              // AS the color name could be the same for different third dimensions
              const c = this.productDocument.colors.find(
                (c) =>
                  `${this.$route.query[this.thirdDimensionName]}__${
                    this.$route.query.color
                  }` === c._key
              )

              if (c) {
                this.selectedDimensions.color = this.getColorFromColorKey(
                  c._key
                )
                this.selectedDimensions.third = this.getThirdFromColorKey(
                  c._key
                )

                return
              }
            }
          } else {
            // IF no the third dimension or it is not selectable,
            // THEN we can search the color directly with its color name
            // AS the color name is considered as unique
            const c = this.productDocument.colors.find(
              (color) =>
                this.getColorFromColorKey(color._key) ===
                this.$route.query.color
            )

            if (c) {
              this.selectedDimensions.color = this.getColorFromColorKey(c._key)

              if (this.hasThirdDimension && this.thirdDimensionName) {
                this.selectedDimensions.third = this.getThirdFromColorKey(
                  c._key
                )
              }

              return
            }
          }
        }

        if (this.productIsOnSale) {
          if (this.hasThirdDimension && this.canSelectThirdDimension) {
            this.selectFirstAvailableThirdDimension()

            await this.$nextTick()
          }

          this.selectFirstAvailableColor()
          return
        }

        // IF no color is selected from the URL,
        // THEN we select the first color from the product document
        // AND the third dimension if it exists
        this.selectedDimensions.color = this.productDocument.colors[0]
          ? this.getColorFromColorKey(this.productDocument.colors[0]._key)
          : null

        if (this.hasThirdDimension) {
          this.selectedDimensions.third = this.productDocument.colors[0]
            ? this.getThirdFromColorKey(this.productDocument.colors[0]._key)
            : null
        }
      }
    },
    updateSelectedSize() {
      if (this.$route.query.variant) {
        const size = this.shopifyProduct.variants.find(
          (v) => v.id === parseInt(this.$route.query.variant)
        )

        if (size) {
          this.selectedDimensions.size = size.opt.size.value
        }
      } else if (this.sizesDimension.length === 1) {
        this.selectedDimensions.size = this.sizesDimension[0].opt.size.value
      }
    },
    showInventoryLeft(size) {
      if (this.productStatus === STATUS.STOCK && size) {
        return (
          size.available &&
          size.quantityAvailable < process.env.ALMOST_SOLD_OUT_LIMIT
        )
      }

      return false
    },

    init() {
      this.$nuxt.$on(EVENTS.OPEN_TESTIMONIAL_MODAL, () => {
        this.showTestimonialsModal = true
      })

      this.$nextTick(() => {
        this.$nuxt.$emit(
          EVENTS.PRODUCT_COLOR_UPDATED,
          this.selectedDimensions.color
        )
      })

      if (this.hasPrecoOrStockLink) {
        this.loadPrecoOrStockProduct()
      }
    },

    async loadPrecoOrStockProduct() {
      if (this.productDocument.product_as_preco_or_stock.shopify_product.id) {
        this.loadingPrecoOrStock = true

        this.precoOrStockShopifyProduct = await services.productService
          .getShopifyProductPriceAndMetafield(
            this.productDocument.product_as_preco_or_stock.shopify_product.id,
            this.$i18n.locale
          )
          .then((p) => {
            if (p) {
              return new ShopifyProduct(p)
            }
            return null
          })

        let status = this.productDocument.product_as_preco_or_stock.status
        if (this.precoOrStockShopifyProduct.metafields) {
          let startDate = this.precoOrStockShopifyProduct.metafields.find(
            (m) => m && m.key === 'start_date_web'
          )?.value

          let endDate = this.precoOrStockShopifyProduct.metafields.find(
            (m) => m && m.key === 'start_date_web'
          )?.value

          if (startDate && endDate) {
            startDate = new Date(startDate)
            endDate = new Date(endDate)

            if (
              isWithinInterval(new Date(), { start: startDate, end: endDate })
            ) {
              status = STATUS.STOCK
            }
          }
        }

        this.precoOrStockShopifyProduct.status = status
        this.loadingPrecoOrStock = false
      }
    },

    onColorChange(_, color) {
      if (
        this.hasThirdDimension &&
        !this.canSelectThirdDimension &&
        color[this.thirdDimensionName]
      ) {
        this.selectedDimensions.third = color[this.thirdDimensionName]
      }

      this.updateState(true)
    },
    onThirdDimensionChange(value) {
      this.selectedDimensions.third = value

      if (this.sizesDimension.length === 0) {
        this.selectFirstAvailableColor()
      }

      this.updateState(true)
    },

    selectFirstAvailableThirdDimension() {
      const firstAvailableThirdDimension = this.thirdDimensions.find(
        (d) => d.available
      )

      if (firstAvailableThirdDimension) {
        this.selectedDimensions.third = firstAvailableThirdDimension.value
      } else {
        this.selectedDimensions.third = this.thirdDimensions[0].value
      }
    },
    selectFirstAvailableColor() {
      const firstAvailableColor = this.colorsDimension.find((c) => c.available)

      if (firstAvailableColor) {
        this.selectedDimensions.color = firstAvailableColor.value

        if (this.hasThirdDimension && !this.canSelectThirdDimension) {
          this.selectedDimensions.third =
            firstAvailableColor[this.thirdDimensionName]
        }
      } else if (this.colorsDimension.length > 0) {
        this.selectedDimensions.color = this.colorsDimension[0].value

        if (this.hasThirdDimension && !this.canSelectThirdDimension) {
          this.selectedDimensions.third =
            this.colorsDimension[0][this.thirdDimensionName]
        }
      }
    },

    updateState(fromUserAction = false) {
      this.$nuxt.$emit(
        EVENTS.PRODUCT_COLOR_UPDATED,
        this.getColorKeyFromDimensions(this.selectedDimensions)
      )

      this.updateSelectedColorScroll()

      if (fromUserAction) {
        this.updateUrlParams()
      }
    },

    handleShowSizeSelector() {
      if (this.$vssWidth < 769) {
        this.showSizeSelector = !this.showSizeSelector
      } else {
        this.showSizeSelectorDesktop = !this.showSizeSelectorDesktop
      }

      this.errors.size = ERRORS_TYPE.NONE
      this.errors.addToCart = ERRORS_TYPE.NONE

      this.$nextTick(() => {
        this.updateSelectedColorScroll()
      })
    },
    handleSelectSize(size) {
      this.selectedDimensions.size = size
      this.errors.size = ERRORS_TYPE.NONE
      this.errors.addToCart = ERRORS_TYPE.NONE

      if (this.productStatus !== STATUS.STOCK) {
        this.showSizeSelectorDesktop = false
      }
    },
    async onSizeDesktopBeforeEnter(el) {
      const { gsap } = await import('gsap' /* webpackChunkName: 'gsap' */)

      gsap.set(el, {
        height: 0,
        opacity: 0,
        overflow: 'hidden',
      })
    },
    async onSizeDesktopEnter(el, done) {
      const { gsap } = await import('gsap' /* webpackChunkName: 'gsap' */)

      gsap.to(el, {
        height: el.scrollHeight,
        opacity: 1,
        duration: 0.3,
        ease: 'power2.out',
        onComplete: done,
      })
    },
    async onSizeDesktopBeforeLeave(el) {
      const { gsap } = await import('gsap' /* webpackChunkName: 'gsap' */)

      gsap.set(el, {
        overflow: 'hidden',
      })
    },
    async onSizeDesktopLeave(el, done) {
      const { gsap } = await import('gsap' /* webpackChunkName: 'gsap' */)

      gsap.to(el, {
        height: 0,
        opacity: 0,
        duration: 0.3,
        ease: 'power2.out',
        onComplete: done,
      })
    },

    handleReducePurchaseSticky() {
      this.reducePurchase = !this.reducePurchase

      this.$nextTick(() => {
        this.updateHasColorsOverflow()
      })
    },

    /**
     * Gère les paramètres de la route
     */
    updateUrlParams() {
      if (process.client) {
        const url = new URL(window.location.href)

        if (this.selectedVariant) {
          url.searchParams.delete('color')

          if (this.hasThirdDimension) {
            url.searchParams.delete(this.thirdDimensionName)
          }

          url.searchParams.set('variant', this.selectedVariant.id)
        } else {
          url.searchParams.delete('variant')

          if (this.selectedDimensions.color) {
            url.searchParams.set('color', this.selectedDimensions.color)
          } else {
            url.searchParams.delete('color')
          }

          if (this.hasThirdDimension && this.selectedDimensions.third) {
            url.searchParams.set(
              this.thirdDimensionName,
              this.selectedDimensions.third
            )
          } else {
            url.searchParams.delete(this.thirdDimensionName)
          }
        }

        window.history.replaceState(null, null, url)
      }
    },

    /**
     * Ajoute le produit au panier
     */
    addToCart() {
      if (!this.selectedVariant) {
        if (this.$vssWidth < 769 && !this.showSizeSelector) {
          this.showSizeSelector = true
        } else if (this.$vssWidth >= 769 && !this.showSizeSelectorDesktop) {
          this.showSizeSelectorDesktop = true
        } else {
          this.errors.size = ERRORS_TYPE.EMPTY
        }

        return
      }

      const customAttributes = []
      customAttributes.push({ key: '_order_type', value: this.orderType })
      customAttributes.push({
        key: '_delivery',
        value: this.$t(this.deliveryText.ref, this.deliveryText.options),
      })
      customAttributes.push({
        key: '_delivery_last_updated_at',
        value: new Date().toJSON(),
      })
      customAttributes.push({
        key: '_slug',
        value: this.productDocument.uid,
      })
      customAttributes.push({ key: '_locale', value: this.$i18n.locale })
      customAttributes.push({ key: '_basket_date', value: new Date().toJSON() })

      if (this.productDocument.care_guide?.uid) {
        customAttributes.push({
          key: '_guide-entretien',
          value: this.productDocument.care_guide.uid,
        })
      }

      if (this.delivery && !isNaN(this.delivery.getTime())) {
        customAttributes.push({
          key: '_delivery_date',
          value: this.delivery,
        })
      }

      if (this.productHasBeenAccessedFromUpsell) {
        customAttributes.push({
          key: '_upsell',
          value: 'true',
        })
      }

      const cartItem = {
        merchandiseId: ShopifyBase64.encode(
          SHOPIFY_TYPES.VARIANT,
          this.selectedVariant.id
        ),
        quantity: 1,
        attributes: customAttributes,
      }

      this.addItemToCart([cartItem])
        .then((_) => {
          this.$track.addToCart(
            this.productDocument.title,
            this.shopifyProduct,
            this.selectedVariant,
            this.shopifyProduct.currency,
            this.cart
          )

          document
            .getElementById('linkOpenCart')
            .dispatchEvent(new Event('click'))
        })
        .catch((err) => {
          this.$logError.captureException(err)
          this.errors.addToCart = ERRORS_TYPE.FAIL
        })

      if (this.authenticated) {
        return services.laPosteService.addToCart(
          this.customer.email,
          this.productDocument.uid,
          this.$i18n.locale.slice(0, 2)
        )
      }

      if (this.connectedEmail && this.connectedEmail !== '') {
        return services.laPosteService.addToCart(
          this.connectedEmail,
          this.productDocument.uid,
          this.$i18n.locale.slice(0, 2)
        )
      }
    },

    /**
     * active le rendu d'overflow-x pour les couleurs
     */
    updateHasColorsOverflow() {
      if (this.$refs.purchaseColors) {
        const el =
          this.$refs.purchaseColors.getElementsByClassName('picker__elements')
        if (el && el[0] && el[0].scrollWidth > el[0].clientWidth) {
          this.$refs.purchaseColors.classList.add('has-overflow-x')

          el[0].addEventListener('scroll', () => {
            // le scroll est à la fin
            if (el[0].scrollLeft + el[0].clientWidth >= el[0].scrollWidth) {
              this.$refs.purchaseColors.classList.add('scroll-end')
            } else {
              this.$refs.purchaseColors.classList.remove('scroll-end')
            }
          })
        } else {
          this.$refs.purchaseColors.classList.remove('has-overflow-x')

          if (el && el[0]) {
            el[0].removeEventListener('scroll')
          }
        }
      }
    },

    updateSelectedColorScroll() {
      if (this.$refs.purchaseColors) {
        const selectedColor = this.$refs.purchaseColors.getElementsByClassName(
          'picker-item--active'
        )
        if (selectedColor && selectedColor[0]) {
          selectedColor[0].scrollIntoView({
            behavior: 'smooth',
            block: 'center',
            inline: 'center',
          })
        }
      }
    },

    handleShowTestimonials() {
      this.showTestimonialsModal = true
    },

    handleShowModal(type) {
      this.modal.show = true

      if (this.modal.content === null || this.modal.type !== type) {
        this.modal.loading = true

        let id = null
        if (type === 'delivery') {
          id = this.productDocument.delivery_and_returns._ref
        } else if (type === 'size') {
          id = this.productDocument.size_guide._ref
        }

        services.sanityService
          .getById({
            id,
            lang: this.$i18n.locale.slice(0, 2),
          })

          .then((document) => {
            this.modal.content = document
            this.modal.type = type
          })
          .catch((err) => {
            this.$logError.captureException(err)
          })
          .finally(() => {
            this.modal.loading = false
          })
      }
    },
    handleHideModal() {
      if (this.modal.show || this.showTestimonialsModal) {
        this.modal.show = false
        this.showTestimonialsModal = false
      } else {
        this.showSizeSelector = false
      }
    },

    handleOverlayClick() {
      if (this.showSizeSelector) {
        this.handleShowSizeSelector()
      }
    },

    /**
     * Kleep
     */
    initKleep() {
      const s = document.createElement('script')
      s.type = 'text/javascript'
      s.async = true
      s.src = 'https://module.kleep.ai/api/widget'
      s.id = 'kleepWidget'
      s.onload = () => {
        this.runKleep()
      }
      document.body.appendChild(s)
    },
    runKleep() {
      if (typeof window.kleep !== 'undefined') {
        kleep.load({
          domain: 'asphalte.com',
          lang: this.$i18n.locale.slice(0, 2),
          selectors: {
            sizing:
              this.$vssWidth < 769
                ? '#kleep-module-sizing'
                : '#kleep-module-sizing-desktop',
          },
          product_id: `${this.shopifyProduct.id}`,
          getSizes: () => {
            const orderedColors = this.colorsDimension.map((c) => c.value)

            return this.shopifyProduct.variants
              .map((v) => ({
                color: v.opt.color.value,
                label: v.opt.size.value,
                variantId: `${v.id}`,
                quantity: this.productIsOnSale ? v.available : 0,
              }))
              .sort((a, b) => {
                if (orderedColors.length > 0) {
                  if (
                    orderedColors.indexOf(a.color.value) <
                    orderedColors.indexOf(b.color.value)
                  )
                    return -1
                  if (
                    orderedColors.indexOf(a.color.value) >
                    orderedColors.indexOf(b.color.value)
                  )
                    return 1
                }
                return 0
              })
          },
          addToCart: (variantId) => {
            const v = this.shopifyProduct.variants.find(
              (v) => v.id === parseInt(variantId)
            )

            if (v) {
              this.updateSelectedDimensionsFromVariant(v)
              this.addToCart()
            }
          },
          // selectSize: (variantId) => {
          //   const v = this.shopifyProduct.variants.find(
          //     (v) => v.id === parseInt(variantId)
          //   )
          //   if (v) {
          //     this.updateSelectedDimensionsFromVariant(v)
          //   }
          // },
        })
      }
    },
    updateSelectedDimensionsFromVariant(variant) {
      if (variant) {
        this.selectedDimensions.size = variant.opt.size.value
      }
    },
  },
}
</script>

<style lang="scss">
.purchase-sticky {
  transition: all 0.3s var(--asphalte-animation-function);

  @include mq($until: tablet) {
    display: flex;
    flex-direction: column;
    z-index: 1000;
  }

  @include mq($from: tablet) {
    background-color: var(--tertiary-color);
  }

  &.has-full-page-modal {
    z-index: 3000;
  }

  .picker__label-container {
    justify-content: start;

    @include mq($from: tablet) {
      justify-content: space-between;
    }
  }

  .picker__label {
    letter-spacing: 0.02em;
  }

  @include mq($until: tablet) {
    position: fixed;
    justify-content: flex-end;
    inset: auto 0 0;
    padding: calc(var(--spacing) * 2) var(--spacing)
      calc(env(safe-area-inset-bottom) + var(--spacing));
    background: linear-gradient(
      180deg,
      RGB(0 0 0 / 0) 0%,
      RGB(0 0 0 / 0.5) 100%
    );

    .overlay {
      position: relative;
      flex: 1 0 auto;
      pointer-events: none;

      &::after {
        content: '';
        position: absolute;
        inset: calc(var(--spacing) * -2) calc(var(--spacing) * -1) 0;
      }
    }

    .purchase__inner {
      background: RGB(0 0 0 / 0.2);
      backdrop-filter: blur(12.5px);
      padding: calc(var(--spacing) * 0.4);
      transition: all 0.3s var(--asphalte-animation-function);

      h1 {
        font-size: var(--base-font-size);
        letter-spacing: 0;
        margin-bottom: 0;
      }
    }

    .purchase__title {
      height: rem(26px);
      transition: all 0.3s var(--asphalte-animation-function);
      gap: calc(var(--spacing) * 0.6);
    }

    .title-with-products-links {
      h1 {
        @include mq($until: tablet) {
          font-size: var(--small-font-size);
        }
      }
    }

    .prices {
      gap: calc(var(--spacing) * 0.6);
      margin-left: auto;
    }

    .price__price {
      font-size: var(--base-font-size);
    }

    .price__text {
      display: none;
    }

    .price--compare {
      color: RGB(255 255 255 / 0.6);
    }

    .picker__label {
      font-size: var(--extra-small-font-size);
      line-height: var(--small-font-size);
      color: var(--tertiary-color);
      font-weight: 600;
      margin-bottom: calc(var(--spacing) * 0.6);
      letter-spacing: 0.2px;
    }
  }

  @include mq($from: tablet) {
    justify-content: flex-start;

    .purchase-sticky__inner {
      padding: calc(var(--spacing) * 3.2) calc(var(--spacing) * 2.8);
    }

    .button-reducer {
      display: none;
    }

    h1 {
      font-size: rem(36px);
      letter-spacing: 0.3;
      margin-bottom: 0;
    }

    .purchase__title {
      padding: 0;
      flex-direction: column;
      align-items: flex-start;
      gap: calc(var(--spacing) * 0.6);
    }

    .prices {
      gap: calc(var(--spacing) * 1.6);
    }

    .picker__label {
      .label {
        display: none;
      }
    }
  }

  .button-as-link {
    background-color: transparent;
    border: 0;
    padding: 0;
    margin: 0;
  }

  .add-to-cart {
    width: 100%;
  }

  .purchase-underline {
    /* stylelint-disable */
    text-decoration-line: underline;
    text-decoration-style: solid;
    text-decoration-skip-ink: none;
    text-decoration-thickness: 2%; /* 0.28px */
    text-underline-offset: auto;
    text-underline-position: from-font;
    text-decoration-color: currentColor;
    /* stylelint-enable */
  }

  .button-reducer {
    padding: calc(var(--spacing) * 0.6) calc(var(--spacing) * 0.8);
    background: RGB(0 0 0 / 0.2);
    backdrop-filter: blur(12.5px);
    position: relative;
    z-index: 3;
    transition: all 0.3s var(--asphalte-animation-function);

    .icon {
      display: block;
      width: rem(16px);
      height: rem(16px);
      transition: all 0.3s var(--asphalte-animation-function);
    }
  }

  .purchase__selectors {
    max-width: 100%;
    gap: calc(var(--spacing) * 0.6);
    transition: all 0.3s var(--asphalte-animation-function), padding-right 0s;
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    padding: calc(var(--spacing) * 0.6) calc(var(--spacing) * 0.4) 0;

    @include mq($from: tablet) {
      flex-direction: column;
      padding: calc(var(--spacing) * 2) 0 0;
      gap: calc(var(--spacing) * 2);
    }
  }

  .purchase__colors {
    flex: 1 1 auto;
    position: relative;

    .picker-item--bg {
      --line-color: RGB(255 255 255 / 0);
    }

    @include mq($until: tablet) {
      overflow: hidden;

      &.has-overflow-x {
        &::before {
          content: '';
          position: absolute;
          bottom: 0;
          right: 0;
          width: rem(9px);
          height: rem(32px);
          background: linear-gradient(
            90deg,
            RGB(0 0 0 / 0) 0%,
            RGB(0 0 0 / 0.2) 100%
          );
          transition: opacity 0.3s;
        }

        &::after {
          content: '';
          position: absolute;
          bottom: 0;
          right: 0;
          width: rem(1px);
          height: rem(32px);
          background-color: #f5f5f5;
          opacity: 0.2;
        }

        &.scroll-end {
          &::before {
            opacity: 0;
          }
        }
      }

      .picker__label {
        height: rem(14px);
        transition: all 0.3s var(--asphalte-animation-function);
      }

      .picker-item--active {
        --line-color: var(--tertiary-color);
        --line-position: #{rem(-3px)};
      }
    }

    @include mq($from: tablet) {
      width: 100%;
      order: 2;

      .picker__label {
        margin-bottom: calc(var(--spacing) * 0.6);
        line-height: rem(20px);
      }

      .picker-item--active {
        --line-color: var(--secondary-color);
        --line-position: #{rem(-5px)};
      }
    }

    .picker.picker--color {
      .picker__elements {
        width: 100%;
        padding: rem(1px);
        padding-right: calc(var(--spacing) * 0.4);
        justify-content: flex-start;
        margin: 0;

        @include mq($until: tablet) {
          flex-wrap: nowrap;
          overflow-x: auto;
          -ms-overflow-style: none;
          scrollbar-width: none;
          gap: 0 calc(var(--spacing) * 0.4);

          &::-webkit-scrollbar {
            display: none;
          }
        }

        @include mq($from: tablet) {
          gap: calc(var(--spacing) * 0.4) calc(var(--spacing) * 0.6);
        }
      }
    }

    .picker--color {
      margin-bottom: 0;

      .picker__element {
        margin: 0;

        &.break {
          display: none;
        }
      }
    }

    .picker-item__bg::after {
      @include mq($until: tablet) {
        box-shadow: 0 0 0 rem(1px) var(--line-color);
      }

      @include mq($from: tablet) {
        box-shadow: inset 0 0 0 rem(2px) var(--line-color);
      }
    }

    .picker-item {
      min-width: rem(32px);
      min-height: rem(32px);
    }
  }

  .purchase__sell-channel {
    overflow: hidden;
    transition: all 0.3s var(--asphalte-animation-function);
    height: rem(24px);
  }

  .purchase__sell-channel__choice {
    position: relative;
    padding: calc(var(--spacing) * 0.2) calc(var(--spacing) * 1);
    background-color: RGB(0 0 0 / 0.05);
    backdrop-filter: blur(12.5px);
    opacity: 0.7;
    font-size: var(--extra-small-font-size);
    font-family: var(--secondary-font-family);
    letter-spacing: 0.2px;
    color: var(--tertiary-color);

    &.nuxt-link-active {
      background-color: RGB(0 0 0 / 0.2);
      opacity: 1;
    }
  }

  .purchase-modal {
    .picker__label {
      color: var(--secondary-color);
      margin-bottom: 0;
    }
  }

  .purchase__thirds,
  .purchase__size-selector {
    @include mq($until: tablet) {
      height: rem(57px);
    }
  }

  .purchase__thirds {
    transition: all 0.3s var(--asphalte-animation-function);

    @include mq($from: tablet) {
      width: 100%;
      order: 1;
    }
  }

  .purchase__size-selector {
    flex-shrink: 0;
    transition: all 0.3s var(--asphalte-animation-function);

    @include mq($from: tablet) {
      width: 100%;
      order: 3;
    }
  }

  .purchase__size-selector-desktop {
    background-color: white;
    border: solid var(--secondary-color-4);
    border-width: 0 rem(1px) rem(1px);

    @include mq($until: tablet) {
      display: none;
    }

    .size-picker {
      padding-top: calc(var(--spacing) * 1.4);
    }
  }

  .purchase__size__guides {
    gap: var(--spacing);
  }

  .purchase__size-picker {
    background-color: transparent;
    border: 0;
    padding: rem(1px) 0;

    @include mq($from: tablet) {
      padding: 0;
      width: 100%;
      background-color: transparent;
      cursor: pointer;

      &.is-open {
        background-color: white;
      }
    }

    .inner {
      @include mq($until: tablet) {
        min-height: rem(32px);
        gap: calc(var(--spacing) * 0.6);
        padding: 0 calc(var(--spacing) * 0.6);
        border-radius: 4px;
        background-color: RGB(245 245 245 / 0.2);
        letter-spacing: 0.2px;
      }

      @include mq($from: tablet) {
        width: 100%;
        min-height: rem(50px);
        padding: rem(1px) var(--spacing) 0;
        background-color: transparent;
        box-shadow: inset 0 0 0 rem(1px) RGB(0 46 93 / 0.2);
        letter-spacing: 0.02em;
        z-index: 1;

        @include on-hover-and-focus {
          box-shadow: inset 0 0 0 rem(1px) RGB(0 46 93 / 1);
        }
      }
    }
  }

  .size-picker__item__quantity {
    position: absolute;
    bottom: calc(var(--spacing) * 0.6);
    left: 0;
    right: 0;
    text-align: center;
    font-size: var(--label-font-size);
    line-height: 1;
    color: var(--secondary-color-2);
    font-weight: 400;
    font-style: normal;
  }

  .size-picker {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    grid-template-rows: auto;
    gap: calc(var(--spacing) * 0.6);

    &--errors {
      background-color: RGB(255 46 0 / 0.1);
    }

    &__item {
      position: relative;
      min-height: rem(56px);
      background-color: #f5f5f5;
      color: var(--secondary-color);
      font-size: var(--small-font-size);
      font-weight: 600;
      border: 0;
      cursor: pointer;

      span {
        position: relative;

        // &::after {
        //   content: '';
        //   position: absolute;
        //   bottom: rem(-2px);
        //   left: 50%;
        //   width: 100%;
        //   height: rem(1px);
        //   background-color: var(--secondary-color);
        //   transform: translateX(-50%);
        // }
      }

      &::after {
        content: '';
        position: absolute;
        inset: 0;
        border: 2px solid RGB(11 75 141 / 0.8);
        opacity: 0;
        transform: scale(1.1);
        transition: opacity 0.3s var(--asphalte-animation-function),
          transform 0s var(--asphalte-animation-function) 0.3s;
      }

      @include on-hover-and-focus {
        &::after {
          opacity: 0.5;
          transform: scale(1);
          transition: all 0.3s var(--asphalte-animation-function);
        }
      }

      &.size-picker__item--active {
        &::after {
          opacity: 1;
          transform: scale(1);
          transition: all 0.3s var(--asphalte-animation-function);
        }
      }

      &.size-picker__item--unavailable {
        position: relative;
        background-color: transparent;
        box-shadow: inset 0 0 0 rem(1px) var(--secondary-color-4);
        pointer-events: none;
        overflow: hidden;

        &::before {
          content: '';
          position: absolute;
          top: 100%;
          left: 0;
          transform: rotate(-25.75deg);
          transform-origin: bottom left;
          width: 120%;
          height: rem(1px);
          background-color: var(--secondary-color-4);
          z-index: 1;
        }

        span {
          z-index: 2;
        }
      }
    }
  }

  .purchase__sold-out {
    @include mq($until: tablet) {
      margin-top: calc(var(--spacing) * 0.6);
      padding-left: calc(var(--spacing) * 0.2);
    }

    @include mq($from: tablet) {
      padding: calc(var(--spacing) * 0.8) calc(var(--spacing) * 1.5);
      box-shadow: inset 0 0 0 rem(1px) var(--secondary-color-4);
      background-color: white;
    }
  }

  .purchase__bottom {
    @include mq($until: tablet) {
      height: rem(37px);
      transition: all 0.3s var(--asphalte-animation-function);

      .icon {
        width: rem(14px);
        height: rem(14px);
      }
    }

    @include mq($from: tablet) {
      letter-spacing: 0.02em;

      .icon {
        width: rem(16px);
        height: rem(16px);
      }
    }

    button {
      background-color: transparent;
      border: 0;
      margin: 0;
      padding: 0;

      @include mq($from: tablet) {
        letter-spacing: 0.02em;
      }
    }
  }

  .kleep-button {
    @include mq($until: tablet) {
      padding: var(--spacing) 0;
    }
  }

  .kleep-cta,
  .kleep-default {
    line-height: 1;

    @include mq($from: tablet) {
      font-family: var(--secondary-font-family);
      font-size: var(--small-font-size) !important;
    }
  }

  .kleep-default {
    color: var(--secondary-color);
  }

  .kleep-default,
  .kleep-underline {
    font-weight: 600;
    text-transform: uppercase;
    padding-bottom: 0;
    /* stylelint-disable */
    text-decoration-line: underline;
    text-decoration-style: solid;
    text-decoration-skip-ink: none;
    text-decoration-color: RGB(0 46 93 / 0.8);
    text-decoration-thickness: 2%; /* 0.28px */
    text-underline-offset: auto;
    text-underline-position: from-font;
    /* stylelint-enable */

    @include mq($until: tablet) {
      font-size: var(--label-font-size) !important;
    }

    @include mq($from: tablet) {
      font-size: var(--small-font-size) !important;
      letter-spacing: 0.02em;
    }

    &::after {
      content: none;
    }
  }

  .kleep-texts {
    .kleep-cta {
      color: RGB(0 46 93 / 0.6);

      @include mq($until: tablet) {
        text-transform: uppercase;
      }

      @include mq($from: tablet) {
        font-family: var(--secondary-font-family);
      }
    }

    .kleep-underline {
      @include mq($until: tablet) {
        font-size: var(--label-font-size) !important;
      }

      @include mq($from: tablet) {
        font-size: var(--small-font-size) !important;
      }
    }

    .kleep-custom-res {
      color: var(--secondary-color);
    }
  }

  .purchase-testimonials {
    padding: calc(var(--spacing) * 0.4) calc(var(--spacing) * 1.4)
      calc(var(--spacing) * 1.4);
    gap: calc(var(--spacing) * 1.6);

    .separator {
      width: rem(1px);
      align-self: stretch;
      background-color: var(--secondary-color-4);
    }
  }

  &.size-selector-open {
    height: 100%;
    background-color: RGB(0 0 0 / 0.5);

    .overlay {
      pointer-events: all;
    }
  }

  &.reduced {
    background: transparent;
    padding: 0 var(--spacing)
      calc(env(safe-area-inset-bottom) + calc(var(--spacing)));

    .purchase__inner {
      background: transparent;
      backdrop-filter: blur(0);
      padding-left: 0;
      padding-right: 0;
    }

    .purchase__sell-channel {
      opacity: 0;
      height: 0;
      pointer-events: none;
    }

    .button-reducer {
      transform: translateY(#{rem(40px)});

      .icon {
        transform: rotate(180deg);
      }
    }

    .purchase__title,
    .purchase__bottom {
      padding-top: 0;
      padding-bottom: 0;
      opacity: 0;
      height: 0;
    }

    .purchase__colors .picker__label {
      opacity: 0;
      height: 0;
      margin-bottom: 0;
    }

    .purchase__selectors {
      padding-top: 0;
      padding-right: rem(55px);
    }

    .purchase__thirds,
    .purchase__size-selector {
      opacity: 0;
      width: 0;
      height: 0;
      transform: translateX(30px);
      pointer-events: none;
    }
  }

  // .is-affixed {
  //   .purchase__top {
  //     // padding-top: calc(var(--spacing) * 3);
  //   }
  // }
}

// .purchase-sticky--tabs {
//   @include mq($until: tablet) {
//     .purchase__inner,
//     .purchase-sticky__actions {
//       background-color: white;
//     }

//     .purchase__inner {
//       h1 {
//         color: var(--secondary-color);
//       }
//     }

//     .price__price {
//       color: var(--secondary-color);
//     }

//     .picker__label {
//       @include mq($until: tablet) {
//         color: RGB(11 75 141 / 0.8);
//       }
//     }

//     .purchase__size-picker {
//       .inner {
//         background-color: #f5f5f5;
//         color: var(--secondary-color);
//       }
//     }

//     .purchase__colors .picker-item--active {
//       --line-color: var(--secondary-color);
//     }

//     .purchase-sticky__actions {
//       padding: calc(var(--spacing) * 0.6) calc(var(--spacing) * 0.4)
//         calc(var(--spacing) * 0.4);
//       transition: all 0.3s var(--asphalte-animation-function);
//     }

//     .purchase__sold-out {
//       p {
//         @include mq($until: tablet) {
//           color: var(--secondary-color);
//         }
//       }
//     }

//     .button-reducer {
//       background-color: white;

//       .icon {
//         color: var(--secondary-color);
//       }
//     }

//     &.reduced {
//       .purchase-sticky__actions {
//         background-color: transparent;
//         padding: 0;
//       }

//       .purchase__colors .picker-item--active {
//         --line-color: var(--tertiary-color);
//       }

//       .button-reducer {
//         background: RGB(0 0 0 / 0.2);

//         .icon {
//           color: var(--tertiary-color);
//         }
//       }
//     }
//   }
// }

.purchase-sticky--stock {
  @include mq($from: tablet) {
    .purchase__size-picker {
      display: none;
    }

    .purchase__size-selector-desktop {
      border: 0;
      background: transparent;

      .size-picker {
        padding: 0;
      }

      .size-picker__item {
        background-color: white;
      }

      .size-picker__item.size-picker__item--unavailable {
        background-color: transparent;
      }
    }
  }
}
</style>
